import { FC, useCallback, useEffect, useState } from 'react';
import { Button, Col, Form, Input, Row } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import {
  createInvoiceTemplate,
  fetchInvoiceTemplateById,
  updateInvoiceTemplate
} from 'services/api/invoiceTemplates';
import { InvoiceTemplate } from 'types/api/invoiceTemplates';

import { Loading, PageWrapper } from 'components/ui';
import { showErrorMessage } from 'utils/showErrorMessage';
import { FieldItem } from '../components';

type FieldValues = {
  name: string;
  keys: {
    key: string;
    label: string;
    type: string;
    required: boolean;
  }[];
};

const InvoiceTemplateDetails: FC = () => {
  // navigation
  const params = useParams();
  const navigate = useNavigate();
  // form
  const [form] = Form.useForm();

  const [result, setResult] = useState<InvoiceTemplate | null>(null);
  const [loading, setLoading] = useState(!!params.id);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const id = params.id;

    if (!id) {
      setLoading(false);

      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const row = await fetchInvoiceTemplateById(id);
        if (!cancelled) setResult(row);
      } catch (err) {
        if (!cancelled) showErrorMessage(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [params.id]);

  useEffect(() => {
    if (result) {
      const keys = result.keys?.map(item => ({
        key: item.key,
        required: item.required,
        label: item.label?.en,
        type: item.type
      }));

      form.setFieldsValue({
        name: result.name,
        keys
      });
    }
  }, [form, result]);

  const onFinish = useCallback(
    async (values: FieldValues) => {
      const keys = values.keys.map((item, index) => ({
        key: item.key,
        label: { en: item.label },
        type: Number(item.type),
        required: !!item.required,
        sort_index: index
      }));

      const input = {
        name: values.name,
        keys
      };

      try {
        setSaving(true);

        if (params.id) {
          await updateInvoiceTemplate(params.id, input);
        } else {
          await createInvoiceTemplate(input);
        }

        navigate('/financial/templates');
      } catch (err) {
        showErrorMessage(err);
      } finally {
        setSaving(false);
      }
    },
    [navigate, params.id]
  );

  if (loading) {
    return <Loading />;
  }

  return (
    <PageWrapper
      back
      onClickBack={() => navigate('/financial/templates')}
      title={params?.id ? 'Update template' : 'Create template'}
      extra={[
        <Button
          key="create-button"
          type="primary"
          onClick={form.submit}
          loading={saving}
        >
          {params?.id ? 'Save' : 'Create'}
        </Button>
      ]}
    >
      <Form
        name="object-form"
        layout="vertical"
        size="large"
        form={form}
        onFinish={onFinish}
        initialValues={{
          keys: [
            {
              key: '',
              required: false,
              label: '',
              type: ''
            }
          ]
        }}
      >
        <Row>
          <Col span={24}>
            <Form.Item name="name" label="Name" rules={[{ required: true }]}>
              <Input placeholder="Name" />
            </Form.Item>
            <Form.List name="keys">
              {(fields, { add, remove, move }) => (
                <>
                  <DragDropContext
                    onDragEnd={r =>
                      move(r.source.index, r.destination?.index || 0)
                    }
                  >
                    <Droppable droppableId="droppable">
                      {dropped => (
                        <div
                          {...dropped.droppableProps}
                          style={{ height: 240 * fields.length }}
                          ref={dropped.innerRef}
                        >
                          {fields.map(({ key, name }, index) => {
                            return (
                              <Draggable
                                key={`field-${key}`}
                                draggableId={`field-${key}`}
                                index={index}
                              >
                                {provided => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    style={{
                                      ...provided.draggableProps.style,
                                      userSelect: 'none',
                                      marginBottom: 24
                                    }}
                                  >
                                    <FieldItem
                                      itemKey={key}
                                      index={index}
                                      name={name}
                                      length={fields.length}
                                      remove={remove}
                                    />
                                  </div>
                                )}
                              </Draggable>
                            );
                          })}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                  <Form.Item>
                    <Button onClick={() => add()} block>
                      Add Field
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Col>
        </Row>
      </Form>
    </PageWrapper>
  );
};

export default InvoiceTemplateDetails;
